"use client";

import Link from "next/link";
import { useRef, useState } from "react";
import TurnstileWidget, {
  TurnstileWidgetHandle,
} from "@/app/_components/security/TurnstileWidget";
import { sendFormEvent } from "@/app/_ga4/eventHandler";
import { ContactConfirmationDialog } from "./ContactConfirmationDialog";
import { useContactForm } from "@/hooks/useContactForm";

type ContactFormErrors = {
  fname?: string;
  phone?: string;
  email?: string;
  comment?: string;
  turnstile?: string;
};

export default function ContactForm({
  pageData,
  campaignId,
}: {
  pageData: any;
  campaignId?: string;
}) {
  const contactInfo = pageData.data[0].attributes;

  const [formStarted, setFormStarted] = useState(false);
  const [errors, setErrors] = useState<ContactFormErrors>({});
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef<TurnstileWidgetHandle | null>(null);

  const {
    formData,
    setFormData,
    confirmationDialogOpen,
    confirmationStatus,
    isSubmitting,
    handleCorrectData,
    handleConfirmationDialogClose,
    saveLead,
  } = useContactForm();

  function validateForm() {
    const fullNameRegex = /^[a-zA-Z\s'-]+$/;
    const phoneRegex = /^\d{10}$/;
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

    const newErrors: ContactFormErrors = {};
    if (!formData.fname) newErrors.fname = "Full name is required";
    else if (!fullNameRegex.test(formData.fname))
      newErrors.fname = "Full name must only contain letters and spaces";
    else if (formData.fname.length > 100)
      newErrors.fname = "Full name must not exceed 100 characters";

    if (!formData.phone) newErrors.phone = "Phone is required";
    else if (!phoneRegex.test(formData.phone))
      newErrors.phone = "Invalid phone number";
    else if (formData.phone.length > 10)
      newErrors.phone = "Phone number exceeds maximum length";

    if (!formData.email) newErrors.email = "Email is required";
    else if (!emailRegex.test(formData.email))
      newErrors.email = "Invalid email address";
    else if (formData.email.length > 50)
      newErrors.email = "Email address exceeds maximum length";

    if (!turnstileToken) {
      newErrors.turnstile = "Please complete the verification challenge";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleChange(event: any) {
    if (!formStarted) {
      setFormStarted(true);
      sendFormEvent("CONTACT_FORM", "START");
    }
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!validateForm()) {
      if (process.env.NODE_ENV !== "development") {
        sendFormEvent("CONTACT_FORM", "INVALID/ERROR");
      }
      return;
    }

    const didSaveLead = await saveLead(campaignId, turnstileToken);

    if (didSaveLead) {
      setTurnstileToken("");
      turnstileRef.current?.reset();
    } else {
      turnstileRef.current?.reset();
    }
  }

  return (
    <section className="contact-section">
      <div className="section-padded">
        <h2>{contactInfo.heading}</h2>
        <p>{contactInfo.description}</p>
        <ul>
          <li>
            <span className="label">Phone: </span>
            <Link href={`tel:${contactInfo.phone}`}>{contactInfo.phone}</Link>
          </li>
          <li>
            <span className="label">Email: </span>
            <Link href={`mailto:${contactInfo.email}`}>
              {contactInfo.email}
            </Link>
          </li>
        </ul>
        <div id="google-map">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2929.428180666852!2d-87.80510068414651!3d42.758148979162165!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x880543dbb04d00e7%3A0x4bd6f3adf71e7dd5!2s1711%20South%20St%2C%20Racine%2C%20WI%2053404!5e0!3m2!1sen!2sus!4v1578672964561!5m2!1sen!2sus"
            loading="lazy"
          ></iframe>
        </div>
      </div>

      <div className="right">
        <form className="demo-form contact" onSubmit={handleSubmit}>
          <div className="form-section">
            <div className="form-auxiliary-field">
              <label htmlFor="company">Company</label>
              <input
                type="text"
                id="company"
                name="company"
                tabIndex={-1}
                autoComplete="new-password"
                onChange={handleChange}
                value={formData.company}
              />
            </div>
            {/* Full Name */}
            <div className="input-group">
              <label>
                Full Name
                {errors.fname && <span className="error">{errors.fname}</span>}
              </label>
              <input
                type="text"
                id="fname"
                name="fname"
                placeholder="Full Name"
                onChange={handleChange}
                value={formData.fname}
              />
            </div>

            {/* Email */}
            <div className="input-group">
              <label>
                Email
                {errors.email && <span className="error">{errors.email}</span>}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="your@email.com"
                onChange={handleChange}
                value={formData.email}
              />
            </div>

            {/* Phone */}
            <div className="input-group">
              <label>
                Phone
                {errors.phone && <span className="error">{errors.phone}</span>}
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                placeholder="1234567890"
                onChange={handleChange}
                value={formData.phone}
              />
            </div>

            {/* Comment */}
            <div className="input-group">
              <label>
                {errors.comment && (
                  <span className="error">{errors.comment}</span>
                )}
              </label>
              <textarea
                id="comment"
                name="comment"
                onChange={handleChange}
                value={formData.comment}
                placeholder="How can we help you?"
              ></textarea>
            </div>

            {/* Submit */}
            <div className="input-group">
              {errors.turnstile && (
                <label className="error">
                  <span className="error">{errors.turnstile}</span>
                </label>
              )}
              <TurnstileWidget
                action="contact"
                ref={turnstileRef}
                onTokenChange={(token) => {
                  setTurnstileToken(token);
                  setErrors((currentErrors) => {
                    const { turnstile, ...rest } = currentErrors;

                    return rest;
                  });
                }}
                onTokenClear={() => {
                  setTurnstileToken("");
                }}
              />
            </div>
            <button
              className="form-submit"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting…" : "Submit"}
            </button>
          </div>
        </form>
      </div>

      {confirmationStatus && (
        <ContactConfirmationDialog
          open={confirmationDialogOpen}
          status={confirmationStatus}
          contactEmail={contactInfo.email}
          contactPhone={contactInfo.phone}
          formData={formData}
          onCorrectData={handleCorrectData}
          onOpenChange={handleConfirmationDialogClose}
        />
      )}
    </section>
  );
}
